import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
} from "@angular/core";
import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from "@angular/forms";
import { isEmpty } from "lodash";
import moment from "moment";
import { debounceTime, distinctUntilChanged, map, skip } from "rxjs/operators";

import { ErrorStateMatcher } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import {
	patchEts1SessionSelectionFromTemplate,
	syncEts1SessionFormControls,
} from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import { Ets1SessionColumnVM } from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import {
	enforceEts35CoursePurposeSingleSelection,
	Ets35SessionSectionVM,
	syncEts35SectionSessionControls,
} from "src/app/cbta-web/ets1/session/ets35-session-sections.utils";
import { Ets1AocSessionSection } from "src/app/cbta-web/forms/ts1-v2/ets1-aoc-session-config/ets1-aoc-session-config.component";
import { Ts1SessionRemarkModalComponent } from "src/app/cbta-web/forms/ts1-v2/ts1-session-remark-modal/ts1-session-remark-modal.component";
import { CustomValidationService } from "src/app/cbta-web/services/custom-validation.service";
import { AIRCRAFT_REGISTRATION_PREFIX_BY_AOC, CONSTANT, SIMULATOR_REGISTRATION_OPTIONS_BY_AOC } from "src/app/constant";
import { FormDataOptions, MasterData, StaffDetails } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import {
	applyOwnStaffNumberFailure,
	applyStaffLookupFailure,
	clearOwnStaffNumberFailure,
	clearStaffLookupFailure,
	clearStaffLookupFailureOnValueChange,
	isInstructorOwnStaffNumber,
	isStaffLookupErrorPayload,
	isStaffLookupResponseValid,
	patchStaffLicenseExpiryDate,
} from "src/app/shared/services/staff-lookup.helper";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";
import { applyLicenseFieldRankRules, createSessionRemarksGroup } from "../../shared-v2/base/form.factories";
import {
	applyFlightSectorsToForm,
	createFlightSectorGroup,
	icaoFieldValidators,
	MAX_FLIGHT_SECTORS,
	normalizeIcaoInput,
	patchFlightSimulatorDetailsOntoForm,
	resolveFlightSectorsFromDetails,
	resolveFlightSimAutocompleteOption,
} from "../../shared-v2/flight-simulator-details.payload";
import { clearCandidateStaffDerivedFields } from "../../shared-v2/utils/draft-staff-validation.utils";
import { classroomsForAoc, ETS35_CLASSROOM_FREE_TEXT } from "./ets35-classroom-options";

type SessionItem = { code: string; description: string };
type SessionGroupVM = { code: string; key: string; items: SessionItem[] };
type ExemptHit = {
	groupCode: string; // e.g. "trainingSession"
	groupKey: string; // display name (from VM)
	itemCode: string; // e.g. "RHS"
	description?: string; // e.g. "Right Hand Seat"
	normalizedCode: string; // uppercased, slash/underscore preserved
	controlPath: string; // e.g. "sessionDetails.trainingSession.RHS"
	currentValue: any; // the actual boolean in the control
};

/** Copy the shared TS-3 / TS-5 session selection onto another candidate. */
export function copyCandidateSessionState(source: UntypedFormGroup, target: UntypedFormGroup): void {
	for (const name of ["sessionDetails", "sessionRemarks"]) {
		const from = source.get(name) as UntypedFormGroup | null;
		const to = target.get(name) as UntypedFormGroup | null;
		if (!from || !to) continue;
		to.patchValue(from.getRawValue(), { emitEvent: false });
	}
}

@Component({
	selector: "app-candidate-details-group-flight-v2",
	templateUrl: "./candidate-details-group-flight.component.html",
	styleUrls: ["./candidate-details-group-flight.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CandidateDetailsGroupFlightV2Component implements OnInit, OnChanges, OnDestroy {
	// ------ Form wiring ------
	candidateForm!: UntypedFormGroup; // the candidate row form (passed in)
	parentForm!: UntypedFormArray; // parent array containing all candidates
	parentFormArrayIndex = 0;
	formType!: string | undefined;
	isTS1: boolean = false;
	isTS3: boolean = false;
	isTS5: boolean = false;

	// ------ Display / UX ------
	panelOpenState = false;
	isOnline = true;
	isBootstrapping = true; // prevents firing autosaves while we wire controls

	// flight/sim section disabled flag (used to hide “×” and look fresh)
	flightSimDisabled = false;

	/** When {@link showAircraftSimulatorToggle}: `aircraft` | `simulator` | `classroom` from form control. */
	flightSimMode: "aircraft" | "simulator" | "classroom" = "aircraft";
	readonly classroomFreeTextValue = ETS35_CLASSROOM_FREE_TEXT;

	/** Lowercase AOC code from logged-in instructor (`userDetails` / role service). */
	instructorAocKey = "";

	/** Fixed prefix for aircraft registration (empty if unknown AOC). */
	aircraftRegPrefix = "";

	/** Preset simulator codes as autocomplete options (plus free-text). */
	simulatorRegistrationOptions: FormDataOptions[] = [];

	/** Variable part of aircraft registration; composed into {@link aircraftRegistration} with {@link aircraftRegPrefix}. */
	aircraftSuffixOnly = new UntypedFormControl("");

	/** Surfaces {@link aircraftRegistration} errors on the suffix input (matInput is bound to {@link aircraftSuffixOnly}). */
	readonly aircraftRegistrationErrorStateMatcher: ErrorStateMatcher = {
		isErrorState: () =>
			!!this.aircraftRegistration?.invalid && (this.aircraftSuffixOnly?.touched || this.aircraftRegistration?.touched),
	};

	private updatingAircraftRegFromSuffix = false;
	private flightSimModeSub?: Subscription;
	private classroomFreeTextSub?: Subscription;
	private aircraftSuffixSub?: Subscription;
	private aircraftRegistrationSyncSub?: Subscription;
	private departureIcaoSub?: Subscription;
	private destinationIcaoSub?: Subscription;
	private sectorsIcaoSubs: Subscription[] = [];
	private flightSimFeatureWiredFor: UntypedFormGroup | null = null;
	/** True when the classroom text control was present the last time the mode UI was wired. */
	private flightSimClassroomWired = false;
	private icaoSanitizeWiredFor: UntypedFormGroup | null = null;
	readonly maxFlightSectors = MAX_FLIGHT_SECTORS;
	private readonly sectorFb = new UntypedFormBuilder();

	// ------ Dates ------
	DATE_FORMAT = CONSTANT.DATE_FORMAT;
	today: Date = new Date();
	currentDate: Date = new Date();
	datePickerRef?: ElementRef;
	datePickerInput = new UntypedFormControl(null, { updateOn: "blur" });

	// ------ Sessions data prepared for the template ------
	sessions!: any; // raw sessions input from parent
	sessionGroups: SessionGroupVM[] = []; // normalized groups for UI
	singleCols: SessionItem[][] = []; // single-group layout split into 4 cols
	twoColsLeft?: SessionGroupVM; // 2-group layout left column
	twoColsRight: SessionItem[][] = []; // 2-group layout right (split into 2 cols if needed)
	secondGroupTitle = "";
	twoGroupsCompact = false; // right side <= 4 items

	lastColTitle = ""; // 2-group layout: right title           // 2-group layout: right has <= 4 items
	lastCols?: SessionItem[][] = [];

	private notReqSub?: Subscription;
	private otherSessionSubs: Subscription[] = [];
	private exemptSessionWatchSubs: Subscription[] = [];
	private sessionSyncSubs: Subscription[] = [];
	private candidateCountSub?: Subscription;
	private watchedCandidateCount = 0;

	// Sessions that disable Flight/Simulator fields
	private readonly EXEMPT_SESSIONS = new Set([
		"LFUS",
		"LPC/OPC",
		"LPC_OPC",
		"LPC",
		"OPC",
		"LOFT",
		"FFS",
		"RHS",
		"UPRT",
	]);

	// Sessions and qualifications that disable Flight/Simulator fields for ETS-3 and ETS-5.
	// Note: ICRM/RCRM and DGI/DGR are combined training-session values, not separate.
	// GI, CRMI, and DGI are Qualification codes (catalog stores them lowercase).
	private readonly TS3_TS5_EXEMPT_SESSIONS = new Set([
		"XFCC",
		"REC",
		"ICRM_RCRM", // Combined value only
		"DGI_DGR", // Combined value only
		"GI",
		"CRMI",
		"DGI",
	]);

	private ets1SessionSubs: Array<{ unsubscribe: () => void }> = [];
	private ets35SessionSubs: Array<{ unsubscribe: () => void }> = [];
	/** Admin template is applied once per candidate row; re-applying wipes radio child picks. */
	private ets1TemplateSelectionApplied = false;
	private ets1TemplateCandidateForm: UntypedFormGroup | null = null;

	constructor(
		public sharedService: SharedService,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private cvs: CustomValidationService,
		private cd: ChangeDetectorRef,
		public containerRatingService: ContainerRatingsService,
		private roleService: RoleService,
		private dialog: MatDialog,
		private fb: UntypedFormBuilder,
		private profileService: ProfileService,
	) {}

	// ========== Inputs from parent ==========
	/** When true (TS1 v3, TS3 v2, TS5 v2), shows Aircraft / Simulator toggle beside the Flight / Simulator header. */
	@Input() showAircraftSimulatorToggle = false;

	/** When true, hides flight/simulator fields (used inside candidate accordions when shared block is below). */
	@Input() hideFlightSimulatorSection = false;

	/** When true, renders only the shared flight/simulator block (below all candidates). */
	@Input() hideCandidateFields = false;

	/** When true (TS1 v3), session remarks use preview + modal editor like the admin form. */
	@Input() useSessionRemarkModal = false;

	/** When true (TS1 v3), uses split ICAO/route fields and static "Flight Details" header. */
	@Input() useTs1V3FlightDetails = false;

	/** `ets1-aoc` renders FR-FC-003 v2 session grid; default legacy layout. */
	@Input() sessionLayoutMode: "legacy" | "ets1-aoc" | "ets35-aoc" = "legacy";
	@Input() ets35SessionSections: Ets35SessionSectionVM[] = [];
	/** eTS-3 / eTS-5: Aircraft, Simulator, and Classroom as radio buttons. */
	@Input() flightModeRadios = false;

	@Input() ets1SessionColumns: Ets1SessionColumnVM[] = [];

	/** Admin form template: sessions to pre-check on the rating grid. */
	@Input() ets1TemplateSessionCodes: string[] = [];

	/** Admin form template: default remark text per session code. */
	@Input() ets1TemplateSessionRemarks: Record<string, string> = {};

	/** When true, dropdown fields are select-only (except Simulator Registration). */
	@Input() selectOnlyDropdowns = false;

	/** All candidates — used for sync and exempt-session checks on the shared flight block. */
	@Input() candidatesArray?: UntypedFormArray;

	/** Index of this candidate in the parent array (0 = first/primary candidate). */
	@Input() candidateIndex = 0;

	/** The whole candidate row form. */
	@Input("candidateForm")
	set _(value: any) {
		this.formType = this.containerRatingService.formType;

		if (this.formType == CONSTANT.FORM_TYPE.ts_1) {
			this.isTS1 = true;
		} else if (this.formType == CONSTANT.FORM_TYPE.ts_3) {
			this.isTS3 = true;
		} else if (this.formType == CONSTANT.FORM_TYPE.ts_5) {
			this.isTS5 = true;
		}

		this.candidateForm = value as UntypedFormGroup;
		if (!this.hideCandidateFields) {
			this.ensureSessionFormStructure();
		}
		if (this.shouldRenderFlightSimulatorSection) {
			this.initFlightDetails();
			this.ensureFlightSimFormStructure();
			this.attachFlightSimToggleWatcher();
			this.refreshInstructorAocAndSimulatorCatalog();
			this.wireFlightSimToggleUi();
			setTimeout(() => this.normalizeAcTypeControlValue(), 0);
		}

		// this.maybeApplyInitialDisablement();
	}

	/** Raw sessions config (group + details) to render the checkboxes. */
	@Input("sessions")
	set sessionsInput(value: any) {
		this.sessions = value;
		this.rebuildSessionLayoutFromInput();
		this.ensureSessionFormStructure();
		this.wireUpNotRequiredMutualExclusion();
		this.cd.detectChanges();
	}

	initFlightDetails() {
		const sd = this.candidateForm?.value?.sessionDetails ?? {};
		// collect ALL keys across all groups where the value is true
		const selected: string[] = Object.values(sd).flatMap((group: any) =>
			Object.entries(group ?? {})
				.filter(([, v]) => v === true)
				.map(([k]) => k),
		);

		let hasExempt = false;

		if (this.shouldApplyExemptSessionFlightSimDisablement()) {
			// For TS3 and TS5, check for TS3/TS5 specific exempt sessions
			if (this.isTS3 || this.isTS5) {
				hasExempt = selected.some((code) => {
					const normalizedCode = (code ?? "").toString().toUpperCase().trim();
					// Check both the original format and normalized format (slash/underscore variants)
					return (
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode) ||
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode.replace(/\//g, "_")) ||
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode.replace(/_/g, "/"))
					);
				});
			} else {
				// For other forms (not TS1, TS3, TS5), check for general exempt sessions
				hasExempt =
					!this.isTS1 && selected.some((code) => this.EXEMPT_SESSIONS.has((code ?? "").toString().toUpperCase()));
			}
		}

		this.applyFlightSimDisablement(hasExempt);
	}

	// ========== Lifecycle ==========
	ngOnChanges(changes: SimpleChanges): void {
		if (
			(changes["ets1SessionColumns"] ||
				changes["sessionLayoutMode"] ||
				changes["ets1TemplateSessionCodes"] ||
				changes["ets1TemplateSessionRemarks"]) &&
			this.sessionLayoutMode === "ets1-aoc"
		) {
			this.syncEts1SessionGrid();
		}

		if (
			(changes["ets35SessionSections"] ||
				changes["sessionLayoutMode"] ||
				changes["ets1TemplateSessionCodes"] ||
				changes["ets1TemplateSessionRemarks"]) &&
			this.sessionLayoutMode === "ets35-aoc"
		) {
			this.syncEts35SessionGrid();
		}

		if (
			(changes["showAircraftSimulatorToggle"] ||
				changes["flightModeRadios"] ||
				changes["hideFlightSimulatorSection"] ||
				changes["hideCandidateFields"] ||
				changes["useTs1V3FlightDetails"] ||
				changes["candidatesArray"]) &&
			this.candidateForm
		) {
			if (this.shouldRenderFlightSimulatorSection) {
				this.ensureFlightSimFormStructure();
				this.refreshInstructorAocAndSimulatorCatalog();
				this.wireFlightSimToggleUi();
			}
		}
	}

	private syncEts1SessionGrid(): void {
		// Flight-only block shares the candidate form but must not reset session controls.
		if (this.hideCandidateFields) return;

		if (!this.ets1SessionColumns.length || !this.ets1SessionsGroup || !this.ets1SessionRemarksGroup) {
			return;
		}

		if (this.candidateForm !== this.ets1TemplateCandidateForm) {
			this.ets1TemplateSelectionApplied = false;
			this.ets1TemplateCandidateForm = this.candidateForm;
		}

		this.ets1SessionSubs.forEach((sub) => sub.unsubscribe());
		this.ets1SessionSubs = [];
		syncEts1SessionFormControls(
			this.fb,
			this.ets1SessionsGroup,
			this.ets1SessionRemarksGroup,
			this.ets1SessionColumns,
			this.ets1SessionSubs,
		);

		if (this.ets1TemplateSessionCodes.length && !this.ets1TemplateSelectionApplied) {
			patchEts1SessionSelectionFromTemplate(
				this.ets1SessionsGroup,
				this.ets1SessionRemarksGroup,
				this.ets1TemplateSessionCodes,
				this.ets1TemplateSessionRemarks,
				this.ets1SessionColumns,
			);
			this.ets1TemplateSelectionApplied = true;
		}

		this.cd.markForCheck();
	}

	get ets35AocSections(): Ets1AocSessionSection[] {
		return this.ets35SessionSections.flatMap((section) => {
			const sessions = this.sectionSessions(section.code);
			const sessionRemarks = this.sectionRemarks(section.code);
			if (!sessions || !sessionRemarks) return [];
			return [
				{
					code: section.code,
					title: section.title,
					columns: section.columns,
					sessions,
					sessionRemarks,
					optionalRemarks: false,
				},
			];
		});
	}

	sectionSessions(sectionCode: string): UntypedFormGroup | null {
		return (this.sessionsDetails?.get(sectionCode) as UntypedFormGroup | null) ?? null;
	}

	sectionRemarks(sectionCode: string): UntypedFormGroup | null {
		return (this.sessionRemarks?.get(sectionCode) as UntypedFormGroup | null) ?? null;
	}

	get classroomOptions(): string[] {
		return classroomsForAoc(this.instructorAocKey);
	}

	get classroomControl(): UntypedFormControl | null {
		return (this.flightSimulatorDetails?.get("classroom") as UntypedFormControl | null) ?? null;
	}

	get classroomFreeTextControl(): UntypedFormControl | null {
		return (this.flightSimulatorDetails?.get("classroomFreeText") as UntypedFormControl | null) ?? null;
	}

	private syncEts35SessionGrid(): void {
		// TS-3 / TS-5 render Session Details on the shared block (hideCandidateFields).
		if (!this.ets35SessionSections.length || !this.sessionsDetails || !this.sessionRemarks) return;

		if (this.candidateForm !== this.ets1TemplateCandidateForm) {
			this.ets1TemplateSelectionApplied = false;
			this.ets1TemplateCandidateForm = this.candidateForm;
		}

		this.ets35SessionSubs.forEach((sub) => sub.unsubscribe());
		this.ets35SessionSubs = [];

		for (const section of this.ets35SessionSections) {
			if (!this.sessionsDetails.get(section.code)) {
				this.sessionsDetails.addControl(section.code, this.fb.group({}), { emitEvent: false });
			}
			if (!this.sessionRemarks.get(section.code)) {
				this.sessionRemarks.addControl(section.code, this.fb.group({}), { emitEvent: false });
			}
			syncEts35SectionSessionControls(
				this.fb,
				section,
				this.sectionSessions(section.code)!,
				this.sectionRemarks(section.code)!,
				this.ets35SessionSubs,
			);
		}

		if (this.ets1TemplateSessionCodes.length && !this.ets1TemplateSelectionApplied) {
			for (const section of this.ets35SessionSections) {
				const sessions = this.sectionSessions(section.code);
				const remarks = this.sectionRemarks(section.code);
				if (!sessions || !remarks) continue;
				patchEts1SessionSelectionFromTemplate(
					sessions,
					remarks,
					this.ets1TemplateSessionCodes,
					this.ets1TemplateSessionRemarks,
					section.columns,
				);
				if (section.code === "course_purpose") {
					enforceEts35CoursePurposeSingleSelection([section], sessions, remarks);
				}
			}
			this.ets1TemplateSelectionApplied = true;
		}

		this.cd.markForCheck();
	}

	ngOnInit(): void {
		this.isOnline = navigator.onLine;

		// online/offline status (if provided)
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
		});

		// find index in parent array
		this.parentForm = (this.candidatesArray ?? (this.candidateForm.parent as UntypedFormArray)) as UntypedFormArray;
		this.parentFormArrayIndex =
			this.parentForm?.controls?.findIndex?.((subform) => subform === this.candidateForm) ?? this.candidateIndex;

		this.staffNumber.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
			clearStaffLookupFailureOnValueChange(this.staffNumber);
			this.rejectOwnStaffNumber();
		});
		this.rejectOwnStaffNumber();

		const initialLicenseExpiry = this.licenseExpiryDate.value;
		if (initialLicenseExpiry) {
			this.datePickerInput.patchValue(moment(initialLicenseExpiry).toDate(), { emitEvent: false });
		}

		// keep datepicker input in sync with licenseExpiryDate (UI only)
		this.licenseExpiryDate.valueChanges.pipe(distinctUntilChanged(), skip(1)).subscribe((val) => {
			if (this.isBootstrapping || !val) return;
			const date = moment(val).toDate();
			this.datePickerInput.patchValue(date, { emitEvent: false });
			this.updateDisplayDateFormat(date);
		});

		// all set
		this.candidateForm.markAsPristine();
		this.isBootstrapping = false;
		if (this.shouldRenderSessionSection) {
			this.setupSessionDetailsSync();
		}
		if (this.shouldRenderFlightSimulatorSection) {
			this.refreshInstructorAocAndSimulatorCatalog();
			this.setupFlightDetailsSync();
			this.wireAcTypeDraftNormalization();
			setTimeout(() => this.normalizeAcTypeControlValue(), 0);
			if (this.isTS3 || this.isTS5) {
				this.attachFlightSimToggleWatcher();
				this.watchCandidateListForExemptSessions();
				this.applyFlightSimDisablement(this.isAnyExemptSessionSelectedAcrossWatchedCandidates());
			}
		}
	}

	/**
	 * Sync flight simulator details from first candidate to all other candidates.
	 * Only the first candidate sets up this watcher and propagates changes.
	 */
	private setupFlightDetailsSync(): void {
		if (!this.hideCandidateFields || !this.parentForm) return;

		const fs = this.flightSimulatorDetails;
		if (!fs) return;

		// Watch for value changes on all flight simulator fields
		const syncToOtherCandidates = (value: Record<string, unknown>) => {
			const otherCandidates = (this.parentForm?.controls ?? []) as UntypedFormGroup[];
			otherCandidates.forEach((candidate) => {
				if (candidate === this.candidateForm) return;

				const otherFs = candidate.get("flightSimulatorDetails") as UntypedFormGroup | null;
				if (!otherFs) return;

				this.ensureSiblingFlightControls(otherFs);
				patchFlightSimulatorDetailsOntoForm(otherFs, value, {
					required: false,
					fb: this.sectorFb,
				});
				if (this.usesEts35FlightModeRules()) {
					this.applyEts35FlightModeValidators(otherFs, this.currentFlightSimMode());
				}
			});
		};

		fs.valueChanges
			.pipe(
				debounceTime(100),
				distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
			)
			.subscribe((value) => {
				if (this.isBootstrapping) return;
				syncToOtherCandidates(value);
			});

		syncToOtherCandidates(fs.getRawValue() as Record<string, unknown>);
	}

	/** Shared TS-3 / TS-5 session block edits candidate 1 and copies that selection to the rest. */
	private setupSessionDetailsSync(): void {
		if (!this.hideCandidateFields || !this.parentForm || !(this.isTS3 || this.isTS5)) return;

		this.sessionSyncSubs.forEach((sub) => sub.unsubscribe());
		this.sessionSyncSubs = [];

		const sync = () => {
			const source = this.candidateForm;
			for (const candidate of (this.parentForm?.controls ?? []) as UntypedFormGroup[]) {
				if (candidate === source) continue;
				copyCandidateSessionState(source, candidate);
			}
		};

		for (const name of ["sessionDetails", "sessionRemarks"]) {
			const group = this.candidateForm.get(name);
			if (!group) continue;
			this.sessionSyncSubs.push(
				group.valueChanges.subscribe(() => {
					if (this.isBootstrapping) return;
					sync();
				}),
			);
		}

		sync();
	}

	private getExemptHits(): ExemptHit[] {
		const sd = this.sessionsDetails;
		if (!sd) return [];

		const hits: ExemptHit[] = [];

		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;

			for (const item of g.items) {
				const ctrl = grp.get(item.code) as UntypedFormControl | null;
				if (!ctrl) continue;

				const normalized = (item.code ?? "").toString().trim().toUpperCase();
				if (ctrl.value === true && this.EXEMPT_SESSIONS.has(normalized)) {
					hits.push({
						groupCode: g.code,
						groupKey: g.key,
						itemCode: item.code,
						description: item.description,
						normalizedCode: normalized,
						controlPath: `sessionDetails.${g.code}.${item.code}`,
						currentValue: ctrl.value,
					});
				}
			}
		}

		return hits;
	}

	ngOnDestroy(): void {
		this.ets1SessionSubs.forEach((sub) => sub.unsubscribe());
		this.ets1SessionSubs = [];
		this.ets35SessionSubs.forEach((sub) => sub.unsubscribe());
		this.ets35SessionSubs = [];
		this.teardownSessionSubscriptions();
		this.teardownFlightSimToggleUi();
		this.teardownIcaoInputSanitization();
		this.exemptSessionWatchSubs.forEach((sub) => sub.unsubscribe());
		this.exemptSessionWatchSubs = [];
		this.sessionSyncSubs.forEach((sub) => sub.unsubscribe());
		this.sessionSyncSubs = [];
		this.candidateCountSub?.unsubscribe();
	}

	// ========== Small layout helpers ==========
	private splitIntoColumns<T>(items: T[], columns: number): T[][] {
		if (columns <= 0) return [];
		if (columns === 1) return [items.slice()];

		if (columns === 2) {
			const n = items.length;
			// Even-ish split, but first column must have at least 5 (if available)
			const firstCount = Math.min(n, Math.max(5, Math.ceil(n / 2)));
			return [items.slice(0, firstCount), items.slice(firstCount)];
		}

		// TS1-v3: fill top-to-bottom per column so A–Z reads down, then across
		if (this.useTs1V3FlightDetails) {
			const cols: T[][] = Array.from({ length: columns }, () => []);
			const perCol = Math.ceil(items.length / columns);
			items.forEach((item, idx) => {
				const colIdx = Math.min(Math.floor(idx / perCol), columns - 1);
				cols[colIdx].push(item);
			});
			return cols;
		}

		// Other forms: round-robin (left-to-right across columns)
		const cols: T[][] = Array.from({ length: columns }, () => []);
		items.forEach((item, idx) => cols[idx % columns].push(item));
		return cols;
	}

	private teardownSessionSubscriptions() {
		if (this.notReqSub) {
			this.notReqSub.unsubscribe();
			this.notReqSub = undefined;
		}
		this.otherSessionSubs.forEach((s) => s.unsubscribe());
		this.otherSessionSubs = [];
	}

	private findNotRequiredControl(): UntypedFormControl | null {
		const sd = this.sessionsDetails;
		if (!sd) return null;
		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;
			const c = grp.get("not_required") as UntypedFormControl | null;
			if (c) return c;
		}
		return null;
	}

	private wireUpNotRequiredMutualExclusion() {
		this.teardownSessionSubscriptions();
		if (!this.shouldRenderSessionSection) return;
		const sd = this.sessionsDetails;
		if (!sd) return;

		const notReq = this.findNotRequiredControl();
		if (!notReq) return;

		// If not_required → true, uncheck everything else (in all groups)
		this.notReqSub = notReq.valueChanges.subscribe((val: boolean) => {
			if (val !== true) return;
			for (const g of this.sessionGroups) {
				const grp = sd.get(g.code) as UntypedFormGroup | null;
				if (!grp) continue;
				Object.keys(grp.controls).forEach((k) => {
					if (k === "not_required") return;
					const c = grp.get(k) as UntypedFormControl;
					if (c?.value === true) c.setValue(false, { emitEvent: false });
				});
			}
			// fire once after batch changes
			sd.updateValueAndValidity({ emitEvent: true });
		});

		// If any other session → true, force not_required → false
		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;
			Object.keys(grp.controls).forEach((k) => {
				if (k === "not_required") return;
				const c = grp.get(k) as UntypedFormControl;
				const sub = c.valueChanges.subscribe((val: boolean) => {
					if (val === true && notReq.value === true) {
						notReq.setValue(false, { emitEvent: false });
						sd.updateValueAndValidity({ emitEvent: true });
					}
					this.cd.markForCheck();
				});
				this.otherSessionSubs.push(sub);
			});
		}
	}

	/** Convert raw `sessions` input to view-model groups and compute columns. */
	private rebuildSessionLayoutFromInput(): void {
		// Push "others" group to the end (unchanged behavior you wanted earlier)
		const isOthers = (s: any) => s.name?.code?.toLowerCase() === "other";

		const sessionsRaw = this.sessions || [];
		const sessions = [...sessionsRaw.filter((s: any) => !isOthers(s)), ...sessionsRaw.filter(isOthers)];

		const groups: SessionGroupVM[] = (sessions ?? []).map((g: any) => ({
			code: g?.name?.code ?? "group",
			key: g?.name?.description ?? "Group",
			items: (g?.details ?? []).map((d: any) => ({
				code: d?.code,
				description: d?.description,
			})),
		}));

		this.sessionGroups = groups;

		if (this.sessionGroups.length === 1) {
			this.singleCols = this.splitIntoColumns(this.sessionGroups[0].items, 4);
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		} else if (this.sessionGroups.length === 4) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const lastItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
			this.lastCols = this.splitIntoColumns(lastItems, 2);
			this.lastColTitle = this.sessionGroups[2].key;
		} else if (this.sessionGroups.length == 3) {
			this.twoColsLeft = this.sessionGroups[0];
			const rightItems = this.sessionGroups[1].items;
			const lastItems = this.sessionGroups[2].items;
			this.twoGroupsCompact = rightItems.length <= 4;
			this.twoColsRight = this.twoGroupsCompact ? [] : this.splitIntoColumns(rightItems, 2);
			this.singleCols = [];
			this.secondGroupTitle = this.sessionGroups[1].key;
			this.lastCols = this.splitIntoColumns(lastItems, 2);
			this.lastColTitle = this.sessionGroups[2].key;
		} else {
			this.singleCols = [];
			this.twoColsLeft = undefined;
			this.twoColsRight = [];
			this.twoGroupsCompact = false;
			this.secondGroupTitle = "";
		}

		this.wireUpNotRequiredMutualExclusion();
	}

	/**
	 * Ensure the nested FormGroup structure exists for Sessions:
	 * candidateForm.sessionDetails.<groupCode>.<detailCode>: boolean
	 */
	private ensureSessionFormStructure(): void {
		if (!this.candidateForm) return;
		// Candidate cards for TS-3 / TS-5 hide Session Details. Rebuilding them in legacy mode
		// would strip radio-child controls owned by the shared block.
		if (!this.shouldRenderSessionSection) return;

		const sd = this.sessionsDetails;
		if (!sd) return;

		const variants = (code: string) => {
			const c = (code ?? "").trim();
			const set = new Set([c, c.replace(/\//g, "_"), c.replace(/_/g, "/")]);
			return Array.from(set);
		};

		for (const g of this.sessionGroups) {
			if (!sd.get(g.code)) {
				sd.addControl(g.code, new UntypedFormGroup({}), { emitEvent: false });
			}
			const grp = sd.get(g.code) as UntypedFormGroup;

			for (const item of g.items) {
				const key = item.code;
				const alts = variants(key);

				// If the exact key doesn't exist, look for an alias (e.g. LPC_OPC vs LPC/OPC)
				if (!grp.get(key)) {
					const aliasKey = alts.find((k) => !!grp.get(k));
					if (aliasKey) {
						const val = (grp.get(aliasKey) as UntypedFormControl).value;
						grp.removeControl(aliasKey, { emitEvent: false });
						grp.addControl(key, new UntypedFormControl(val), { emitEvent: false });
					} else {
						grp.addControl(key, new UntypedFormControl(false), { emitEvent: false });
					}
				}
			}

			// ETS1 v2 catalog owns ts_1 controls (parent + radio children); legacy VM is parent-only.
			if (this.sessionLayoutMode === "legacy") {
				const validKeys = new Set(g.items.map((i) => i.code));
				Object.keys(grp.controls).forEach((k) => {
					if (!validKeys.has(k)) grp.removeControl(k, { emitEvent: false });
				});
			}
		}

		sd.updateValueAndValidity({ emitEvent: false });
		this.candidateForm.updateValueAndValidity({ emitEvent: false });

		// Also ensure session remarks structure exists
		this.ensureSessionRemarksStructure();

		if (this.sessionLayoutMode === "ets1-aoc" && this.ets1SessionColumns.length) {
			this.syncEts1SessionGrid();
		}
		if (this.sessionLayoutMode === "ets35-aoc" && this.ets35SessionSections.length) {
			this.syncEts35SessionGrid();
		}
	}

	/**
	 * Ensure the nested FormGroup structure exists for Session Remarks:
	 * candidateForm.sessionRemarks.<groupCode>.<detailCode>: string
	 */
	private ensureSessionRemarksStructure(): void {
		if (!this.candidateForm) return;

		// Create sessionRemarks group if it doesn't exist
		if (!this.candidateForm.get("sessionRemarks")) {
			const remarksGroup = createSessionRemarksGroup(new UntypedFormBuilder(), this.sessions ?? []);
			this.candidateForm.addControl("sessionRemarks", remarksGroup, { emitEvent: false });
		}

		const sr = this.sessionRemarks;
		if (!sr) return;

		// Ensure all groups and controls exist
		for (const g of this.sessionGroups) {
			if (!sr.get(g.code)) {
				sr.addControl(g.code, new UntypedFormGroup({}), { emitEvent: false });
			}
			const grp = sr.get(g.code) as UntypedFormGroup;

			for (const item of g.items) {
				const key = item.code;
				if (!grp.get(key)) {
					grp.addControl(key, new UntypedFormControl(""), { emitEvent: false });
				}
			}

			if (this.sessionLayoutMode === "legacy") {
				const validKeys = new Set(g.items.map((i) => i.code));
				Object.keys(grp.controls).forEach((k) => {
					if (!validKeys.has(k)) grp.removeControl(k, { emitEvent: false });
				});
			}
		}

		sr.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Get the remark control for a specific session item.
	 */
	getSessionRemarkControl(groupCode: string, itemCode: string): UntypedFormControl {
		const sr = this.sessionRemarks;
		if (!sr) return new UntypedFormControl("");
		const grp = sr.get(groupCode) as UntypedFormGroup | null;
		if (!grp) return new UntypedFormControl("");
		const ctrl = grp.get(itemCode) as UntypedFormControl | null;
		return ctrl ?? new UntypedFormControl("");
	}

	/**
	 * Check if a session checkbox is selected.
	 */
	isSessionSelected(groupCode: string, itemCode: string): boolean {
		const sd = this.sessionsDetails;
		if (!sd) return false;
		const grp = sd.get(groupCode) as UntypedFormGroup | null;
		if (!grp) return false;
		const ctrl = grp.get(itemCode) as UntypedFormControl | null;
		return ctrl?.value === true;
	}

	getSessionRemarkPreview(groupCode: string, itemCode: string): string {
		const value = (this.getSessionRemarkControl(groupCode, itemCode).value ?? "").toString().trim();
		if (!value) return "";
		return value.length > 20 ? `${value.slice(0, 20)}…` : value;
	}

	openSessionRemarkEditor(groupCode: string, session: SessionItem): void {
		const ctrl = this.getSessionRemarkControl(groupCode, session.code);
		const dialogRef = this.dialog.open(Ts1SessionRemarkModalComponent, {
			panelClass: ["ts1-session-remark-modal-panel", "ts1-session-remark-modal"],
			width: "480px",
			hasBackdrop: true,
			data: {
				sessionLabel: session.description,
				value: (ctrl.value ?? "").toString(),
			},
		});

		dialogRef.afterClosed().subscribe((result: string | null) => {
			if (result === null) return;
			ctrl.setValue(result);
			ctrl.markAsDirty();
			this.cd.detectChanges();
		});
	}

	/**
	 * Ensure the nested FormGroup structure exists for Flight/Simulator details.
	 * Split UI (TS1 v3 / TS3 v2 / TS5 v2): {@link aircraftRegistration}, {@link simulatorRegistration}, {@link aircraftOrSimulator}.
	 * Legacy: {@link aircraftSimReg}.
	 */
	private ensureFlightSimFormStructure(): void {
		if (!this.candidateForm) return;

		if (!this.candidateForm.get("flightSimulatorDetails")) {
			this.candidateForm.addControl("flightSimulatorDetails", new UntypedFormGroup({}), { emitEvent: false });
		}

		const fs = this.flightSimulatorDetails;

		const ensure = (name: string) => {
			if (!fs.get(name)) {
				fs.addControl(name, new UntypedFormControl(""), { emitEvent: false });
			}
		};

		ensure("acType");
		ensure("sectorNo");
		ensure("accumulatedSectors");

		if (this.useTs1V3FlightDetails) {
			ensure("departureIcao");
			ensure("destinationIcao");
			ensure("otherAirportsRoute");
			const existingSectors = fs.get("sectors");
			if (!(existingSectors instanceof UntypedFormArray) || existingSectors.length === 0) {
				if (existingSectors && !(existingSectors instanceof UntypedFormArray)) {
					fs.removeControl("sectors", { emitEvent: false });
				}
				const legs = resolveFlightSectorsFromDetails(fs.getRawValue());
				applyFlightSectorsToForm(fs, legs.length ? legs : [{ departureIcao: "", destinationIcao: "" }], {
					required: this.isTS1,
					fb: this.sectorFb,
				});
			}
			if (fs.get("airportRoute")) {
				fs.removeControl("airportRoute", { emitEvent: false });
			}
		} else {
			ensure("airportRoute");
			if (fs.get("departureIcao")) {
				fs.removeControl("departureIcao", { emitEvent: false });
			}
			if (fs.get("destinationIcao")) {
				fs.removeControl("destinationIcao", { emitEvent: false });
			}
			if (fs.get("otherAirportsRoute")) {
				fs.removeControl("otherAirportsRoute", { emitEvent: false });
			}
			if (fs.get("sectors")) {
				fs.removeControl("sectors", { emitEvent: false });
			}
		}

		if (this.showAircraftSimulatorToggle) {
			ensure("aircraftRegistration");
			ensure("simulatorRegistration");
			if (this.flightModeRadios) {
				ensure("classroom");
				ensure("classroomFreeText");
			}
			if (!fs.get("aircraftOrSimulator")) {
				fs.addControl("aircraftOrSimulator", new UntypedFormControl("aircraft"), { emitEvent: false });
			}
			const legacy = fs.get("aircraftSimReg") as UntypedFormControl | null;
			if (legacy) {
				const legacyVal = (legacy.value ?? "").toString();
				const modeCtrl = fs.get("aircraftOrSimulator") as UntypedFormControl;
				const mode = this.resolveFlightSimMode(modeCtrl.value);
				const ar = fs.get("aircraftRegistration") as UntypedFormControl;
				const sr = fs.get("simulatorRegistration") as UntypedFormControl;
				if (legacyVal && !ar.value && !sr.value) {
					if (mode === "simulator") {
						sr.setValue(legacyVal, { emitEvent: false });
					} else {
						ar.setValue(legacyVal, { emitEvent: false });
					}
				}
				fs.removeControl("aircraftSimReg", { emitEvent: false });
			}
		} else {
			if (fs.get("aircraftRegistration")) {
				fs.removeControl("aircraftRegistration", { emitEvent: false });
			}
			if (fs.get("simulatorRegistration")) {
				fs.removeControl("simulatorRegistration", { emitEvent: false });
			}
			if (fs.get("aircraftOrSimulator")) {
				fs.removeControl("aircraftOrSimulator", { emitEvent: false });
			}
			ensure("aircraftSimReg");
		}

		fs.updateValueAndValidity({ emitEvent: false });
		this.applyFlightSimFieldValidators();
		this.wireIcaoInputSanitization();
	}

	/** Restore required validators when flight/simulator fields are active (cleared while exempt sessions are selected). */
	private applyFlightSimFieldValidators(): void {
		if (this.flightSimDisabled) return;
		this.flightSimMode = this.currentFlightSimMode();

		if (this.usesEts35FlightModeRules() && this.flightSimulatorDetails) {
			this.applyEts35FlightModeValidators(this.flightSimulatorDetails, this.flightSimMode);
			return;
		}

		const setRequired = (ctrl: UntypedFormControl | null | undefined) => {
			if (!ctrl || ctrl.disabled) return;
			ctrl.setValidators([Validators.required]);
			ctrl.updateValueAndValidity({ emitEvent: false });
		};

		if (this.flightSimMode === "classroom") {
			this.clearFlightFieldValidators();
			this.clearSectorIcaoValidators(this.flightSimulatorDetails);
			this.applyClassroomValidators();
			return;
		}

		this.clearClassroomValidators(this.flightSimulatorDetails);

		if (this.isTS1 || this.isTS3 || this.isTS5) {
			setRequired(this.acType);
		}
		if (this.isTS1) {
			if (this.useTs1V3FlightDetails) {
				for (const group of this.sectors.controls) {
					const g = group as UntypedFormGroup;
					const dep = g.get("departureIcao") as UntypedFormControl | null;
					const dest = g.get("destinationIcao") as UntypedFormControl | null;
					if (dep && !dep.disabled) {
						dep.setValidators(icaoFieldValidators(true));
						dep.updateValueAndValidity({ emitEvent: false });
					}
					if (dest && !dest.disabled) {
						dest.setValidators(icaoFieldValidators(true));
						dest.updateValueAndValidity({ emitEvent: false });
					}
				}
				this.syncMirroredIcaoFromFirstSector();
			} else {
				setRequired(this.airportRoute);
			}
			setRequired(this.sectorNo);
			setRequired(this.accumulatedSectors);
			if (!this.showAircraftSimulatorToggle) {
				setRequired(this.aircraftSimReg);
			}
		}

		this.applySplitFlightRegistrationValidators();
	}

	private refreshInstructorAocAndSimulatorCatalog(): void {
		const raw = (this.roleService.getUserAOC() ?? "").toString().trim().toLowerCase();
		this.instructorAocKey = raw;

		const md = this.sharedService.sessionMasterData as MasterData & {
			aircraftRegistrationPrefixesByAoc?: Record<string, string>;
			simulatorRegistrationOptionsByAoc?: Record<string, FormDataOptions[] | string[]>;
		};

		const prefixFromApi = md?.aircraftRegistrationPrefixesByAoc?.[raw]?.trim();
		this.aircraftRegPrefix = (prefixFromApi || AIRCRAFT_REGISTRATION_PREFIX_BY_AOC[raw] || "").trim();

		const apiSim = md?.simulatorRegistrationOptionsByAoc?.[raw];
		const fallback = SIMULATOR_REGISTRATION_OPTIONS_BY_AOC[raw] ?? [];
		this.simulatorRegistrationOptions = this.normalizeSimulatorOptions(apiSim ?? fallback);
		this.applyFlightSimFieldValidators();
	}

	private aircraftRegistrationTailValidator(ctrl: AbstractControl): Record<string, boolean> | null {
		const val = (ctrl.value ?? "").toString().trim();
		if (!val) {
			return null;
		}
		if (this.aircraftRegPrefix) {
			if (!val.startsWith(this.aircraftRegPrefix)) {
				return { aircraftRegPrefix: true };
			}
			const tail = this.normalizeAircraftSuffixInput(val.slice(this.aircraftRegPrefix.length));
			if (tail.length < 3 || tail.length > 5) {
				return { aircraftRegTail: true };
			}
			return null;
		}
		const normalized = this.normalizeAircraftSuffixInput(val);
		if (normalized.length < 3 || normalized.length > 5) {
			return { aircraftRegTail: true };
		}
		return null;
	}

	private applySplitFlightRegistrationValidators(): void {
		if (!this.showAircraftSimulatorToggle || !this.flightSimulatorDetails) {
			return;
		}
		const ar = this.flightSimulatorDetails.get("aircraftRegistration") as UntypedFormControl | null;
		const sr = this.flightSimulatorDetails.get("simulatorRegistration") as UntypedFormControl | null;
		if (!ar || !sr) {
			return;
		}
		ar.clearValidators();
		sr.clearValidators();
		if (this.flightSimDisabled) {
			ar.updateValueAndValidity({ emitEvent: false });
			sr.updateValueAndValidity({ emitEvent: false });
			return;
		}
		if (this.flightSimMode === "classroom") {
			ar.updateValueAndValidity({ emitEvent: false });
			sr.updateValueAndValidity({ emitEvent: false });
			return;
		}
		if (this.flightSimMode === "aircraft") {
			ar.setValidators([Validators.required, (ctrl: AbstractControl) => this.aircraftRegistrationTailValidator(ctrl)]);
		} else {
			sr.setValidators([Validators.required]);
		}
		ar.updateValueAndValidity({ emitEvent: false });
		sr.updateValueAndValidity({ emitEvent: false });
	}

	/** Suffix is alphanumeric, uppercase, max 5 (e.g. 9M-XXF or 9M-XXF1). */
	private normalizeAircraftSuffixInput(raw: unknown): string {
		return (raw ?? "")
			.toString()
			.replace(/[^A-Za-z0-9]/g, "")
			.toUpperCase()
			.slice(0, 5);
	}

	/** Sanitize Departure / Destination ICAO as the user types or when drafts are loaded. */
	private wireIcaoInputSanitization(): void {
		if (!this.useTs1V3FlightDetails || !this.candidateForm) {
			this.teardownIcaoInputSanitization();
			return;
		}

		this.teardownIcaoInputSanitization();
		this.icaoSanitizeWiredFor = this.candidateForm;
		this.wireSectorIcaoSanitization();
		this.normalizeExistingIcaoValues();
	}

	private wireSectorIcaoSanitization(): void {
		this.teardownSectorIcaoSubs();
		for (const group of this.sectors.controls) {
			const g = group as UntypedFormGroup;
			for (const name of ["departureIcao", "destinationIcao"] as const) {
				const ctrl = g.get(name) as UntypedFormControl | null;
				if (!ctrl) continue;
				this.sectorsIcaoSubs.push(
					ctrl.valueChanges.subscribe((raw) => {
						if (this.flightSimDisabled) return;
						const cleaned = normalizeIcaoInput(raw);
						if (cleaned !== (raw ?? "").toString()) {
							ctrl.setValue(cleaned, { emitEvent: false });
						}
					}),
				);
			}
		}
	}

	private teardownSectorIcaoSubs(): void {
		this.sectorsIcaoSubs.forEach((s) => s.unsubscribe());
		this.sectorsIcaoSubs = [];
	}

	private normalizeExistingIcaoValues(): void {
		for (const group of this.sectors.controls) {
			const g = group as UntypedFormGroup;
			for (const name of ["departureIcao", "destinationIcao"] as const) {
				const ctrl = g.get(name) as UntypedFormControl | null;
				if (!ctrl) continue;
				const cleaned = normalizeIcaoInput(ctrl.value);
				if (cleaned !== (ctrl.value ?? "").toString()) {
					ctrl.setValue(cleaned, { emitEvent: false });
				}
			}
		}
		this.syncMirroredIcaoFromFirstSector();
	}

	private teardownIcaoInputSanitization(): void {
		this.departureIcaoSub?.unsubscribe();
		this.departureIcaoSub = undefined;
		this.destinationIcaoSub?.unsubscribe();
		this.destinationIcaoSub = undefined;
		this.teardownSectorIcaoSubs();
		this.icaoSanitizeWiredFor = null;
	}

	private syncMirroredIcaoFromFirstSector(): void {
		const first = this.sectors.at(0) as UntypedFormGroup | null;
		if (!first) return;
		const dep = normalizeIcaoInput(first.get("departureIcao")?.value);
		const dest = normalizeIcaoInput(first.get("destinationIcao")?.value);
		if (this.departureIcao && (this.departureIcao.value ?? "") !== dep) {
			this.departureIcao.setValue(dep, { emitEvent: false });
		}
		if (this.destinationIcao && (this.destinationIcao.value ?? "") !== dest) {
			this.destinationIcao.setValue(dest, { emitEvent: false });
		}
	}

	get canAddFlightSector(): boolean {
		return this.sectors.length < this.maxFlightSectors;
	}

	sectorDepartureControl(index: number): UntypedFormControl | null {
		return (this.sectors.at(index) as UntypedFormGroup | null)?.get("departureIcao") as UntypedFormControl | null;
	}

	sectorDestinationControl(index: number): UntypedFormControl | null {
		return (this.sectors.at(index) as UntypedFormGroup | null)?.get("destinationIcao") as UntypedFormControl | null;
	}

	addFlightSector(): void {
		if (this.flightSimDisabled || !this.canAddFlightSector) return;
		const prevDest = normalizeIcaoInput(this.sectorDestinationControl(this.sectors.length - 1)?.value);
		this.sectors.push(
			createFlightSectorGroup(this.sectorFb, { departureIcao: prevDest, destinationIcao: "" }, this.isTS1),
		);
		this.wireSectorIcaoSanitization();
		this.syncMirroredIcaoFromFirstSector();
		this.flightSimulatorDetails.updateValueAndValidity({ emitEvent: true });
		this.cd.detectChanges();
	}

	removeFlightSector(index: number): void {
		if (this.flightSimDisabled || this.sectors.length <= 1) return;
		this.sectors.removeAt(index);
		this.rechainFlightSectorsFrom(Math.max(0, index - 1));
		this.wireSectorIcaoSanitization();
		this.syncMirroredIcaoFromFirstSector();
		this.flightSimulatorDetails.updateValueAndValidity({ emitEvent: true });
		this.cd.detectChanges();
	}

	onSectorDestinationChange(index: number): void {
		if (this.flightSimDisabled) return;
		const dest = normalizeIcaoInput(this.sectorDestinationControl(index)?.value);
		const nextDep = this.sectorDepartureControl(index + 1);
		if (nextDep && (nextDep.value ?? "").toString() !== dest) {
			nextDep.setValue(dest, { emitEvent: false });
		}
		this.syncMirroredIcaoFromFirstSector();
	}

	onSectorDestinationOrDepartureChange(index: number): void {
		this.onSectorDestinationChange(index);
		this.syncMirroredIcaoFromFirstSector();
	}

	/** After remove/reorder, copy each sector's destination into the next departure. */
	private rechainFlightSectorsFrom(startIndex: number): void {
		for (let i = startIndex; i < this.sectors.length - 1; i++) {
			const dest = normalizeIcaoInput(this.sectorDestinationControl(i)?.value);
			const nextDep = this.sectorDepartureControl(i + 1);
			if (nextDep) {
				nextDep.setValue(dest, { emitEvent: false });
			}
		}
	}

	private normalizeSimulatorOptions(src: FormDataOptions[] | string[]): FormDataOptions[] {
		if (!Array.isArray(src)) return [];
		return src.map((item) => {
			if (typeof item === "string") {
				const s = item.trim();
				return { code: s, description: s };
			}
			const code = (item?.code ?? item?.description ?? "").toString().trim();
			const description = (item?.description ?? item?.code ?? code).toString().trim();
			return { code, description };
		});
	}

	private resolveFlightSimMode(value: unknown): "aircraft" | "simulator" | "classroom" {
		if (value === "simulator") return "simulator";
		if (this.flightModeRadios && value === "classroom") return "classroom";
		return "aircraft";
	}

	private clearControlValidators(ctrl: UntypedFormControl | null | undefined): void {
		if (!ctrl) return;
		ctrl.clearValidators();
		ctrl.updateValueAndValidity({ emitEvent: false });
	}

	private clearFlightFieldValidators(): void {
		this.clearControlValidators(this.acType);
		this.clearControlValidators(this.sectorNo);
		this.clearControlValidators(this.accumulatedSectors);
		this.clearControlValidators(this.airportRoute);
		this.clearControlValidators(this.aircraftRegistration);
		this.clearControlValidators(this.simulatorRegistration);
	}

	/** Latest TS-3 / TS-5: Aircraft, Simulator, and Classroom each require only their own fields. */
	private usesEts35FlightModeRules(): boolean {
		return this.flightModeRadios && (this.isTS3 || this.isTS5);
	}

	private currentFlightSimMode(): "aircraft" | "simulator" | "classroom" {
		const raw = this.flightSimulatorDetails?.get("aircraftOrSimulator")?.value ?? this.flightSimMode;
		return this.resolveFlightSimMode(raw);
	}

	/**
	 * Aircraft: registration and type. Simulator: registration and type. Classroom: room text.
	 * Sector number, accumulated sectors, and ICAO rows stay optional so they cannot block Start Rating.
	 */
	private applyEts35FlightModeValidators(fs: UntypedFormGroup, mode: "aircraft" | "simulator" | "classroom"): void {
		if (mode === "classroom") {
			this.clearNamedFlightValidators(fs, [
				"acType",
				"sectorNo",
				"accumulatedSectors",
				"airportRoute",
				"aircraftRegistration",
				"simulatorRegistration",
				"classroom",
			]);
			this.clearSectorIcaoValidators(fs);
			const freeText = fs.get("classroomFreeText") as UntypedFormControl | null;
			if (freeText && !freeText.disabled) {
				freeText.setValidators([Validators.required]);
				freeText.updateValueAndValidity({ emitEvent: false });
			}
			return;
		}

		this.clearClassroomValidators(fs);
		this.clearNamedFlightValidators(fs, ["sectorNo", "accumulatedSectors", "airportRoute"]);
		this.clearSectorIcaoValidators(fs);

		const acType = fs.get("acType") as UntypedFormControl | null;
		if (acType && !acType.disabled) {
			acType.setValidators([Validators.required]);
			acType.updateValueAndValidity({ emitEvent: false });
		}
		this.applyRegistrationValidatorsOn(fs, mode);
	}

	private clearNamedFlightValidators(fs: UntypedFormGroup, names: string[]): void {
		for (const name of names) {
			this.clearControlValidators(fs.get(name) as UntypedFormControl | null);
		}
	}

	private clearClassroomValidators(fs: UntypedFormGroup | null | undefined): void {
		if (!fs) return;
		this.clearControlValidators(fs.get("classroom") as UntypedFormControl | null);
		this.clearControlValidators(fs.get("classroomFreeText") as UntypedFormControl | null);
	}

	private clearSectorIcaoValidators(fs: UntypedFormGroup | null | undefined): void {
		const sectors = fs?.get("sectors");
		if (!(sectors instanceof UntypedFormArray)) return;
		for (const group of sectors.controls) {
			const row = group as UntypedFormGroup;
			this.clearControlValidators(row.get("departureIcao") as UntypedFormControl | null);
			this.clearControlValidators(row.get("destinationIcao") as UntypedFormControl | null);
		}
	}

	private applyRegistrationValidatorsOn(fs: UntypedFormGroup, mode: "aircraft" | "simulator" | "classroom"): void {
		const ar = fs.get("aircraftRegistration") as UntypedFormControl | null;
		const sr = fs.get("simulatorRegistration") as UntypedFormControl | null;
		ar?.clearValidators();
		sr?.clearValidators();
		if (!this.flightSimDisabled && mode === "aircraft" && ar && !ar.disabled) {
			ar.setValidators([Validators.required, (ctrl: AbstractControl) => this.aircraftRegistrationTailValidator(ctrl)]);
		} else if (!this.flightSimDisabled && mode === "simulator" && sr && !sr.disabled) {
			sr.setValidators([Validators.required]);
		}
		ar?.updateValueAndValidity({ emitEvent: false });
		sr?.updateValueAndValidity({ emitEvent: false });
	}

	private ensureSiblingFlightControls(fs: UntypedFormGroup): void {
		const ensure = (name: string, value: unknown = "") => {
			if (!fs.get(name)) {
				fs.addControl(name, new UntypedFormControl(value), { emitEvent: false });
			}
		};
		if (!this.showAircraftSimulatorToggle) return;
		ensure("aircraftRegistration");
		ensure("simulatorRegistration");
		ensure("aircraftOrSimulator", this.currentFlightSimMode());
		if (this.flightModeRadios) {
			ensure("classroom");
			ensure("classroomFreeText");
		}
	}

	/** Shared flight block is the only editor; new rows must carry the same values and rules. */
	private copyPrimaryFlightDetailsTo(fs: UntypedFormGroup): void {
		const source = this.flightSimulatorDetails;
		if (!source) return;
		this.ensureSiblingFlightControls(fs);
		patchFlightSimulatorDetailsOntoForm(fs, source.getRawValue() as Record<string, unknown>, {
			required: false,
			fb: this.sectorFb,
		});
		if (this.usesEts35FlightModeRules()) {
			this.applyEts35FlightModeValidators(fs, this.currentFlightSimMode());
		}
	}

	private applyClassroomValidators(): void {
		const classroom = this.classroomControl;
		const freeText = this.classroomFreeTextControl;
		this.clearControlValidators(classroom);
		freeText?.setValidators([Validators.required]);
		classroom?.updateValueAndValidity({ emitEvent: false });
		freeText?.updateValueAndValidity({ emitEvent: false });
	}

	/** Keep legacy `classroom` control aligned for draft/API payloads. */
	private syncClassroomStorageFromInput(): void {
		const classroom = this.classroomControl;
		const freeText = this.classroomFreeTextControl;
		if (!classroom || !freeText) return;
		const label = (freeText.value ?? "").toString().trim();
		if (!label) {
			classroom.setValue("", { emitEvent: false });
			return;
		}
		if (this.classroomOptions.includes(label)) {
			classroom.setValue(label, { emitEvent: false });
			return;
		}
		classroom.setValue(this.classroomFreeTextValue, { emitEvent: false });
	}

	/** API stores the classroom label on airportRoute; restore the editable field. */
	private hydrateClassroomFromAirportRoute(): void {
		if (!this.flightModeRadios || this.flightSimMode !== "classroom") return;
		const classroom = this.classroomControl;
		const freeText = this.classroomFreeTextControl;
		if (!classroom || !freeText) return;
		if ((freeText.value ?? "").toString().trim()) return;
		const fromClassroom = (classroom.value ?? "").toString().trim();
		const preset = fromClassroom && fromClassroom !== this.classroomFreeTextValue ? fromClassroom : "";
		const label = (preset || (this.flightSimulatorDetails?.get("airportRoute")?.value ?? "").toString().trim()).trim();
		if (!label) return;
		freeText.setValue(label, { emitEvent: false });
		this.syncClassroomStorageFromInput();
		this.applyClassroomValidators();
	}

	private teardownFlightSimToggleUi(): void {
		this.flightSimModeSub?.unsubscribe();
		this.flightSimModeSub = undefined;
		this.classroomFreeTextSub?.unsubscribe();
		this.classroomFreeTextSub = undefined;
		this.aircraftSuffixSub?.unsubscribe();
		this.aircraftSuffixSub = undefined;
		this.aircraftRegistrationSyncSub?.unsubscribe();
		this.aircraftRegistrationSyncSub = undefined;
		this.flightSimFeatureWiredFor = null;
		this.flightSimClassroomWired = false;
	}

	private wireFlightSimToggleUi(): void {
		if (!this.showAircraftSimulatorToggle || !this.candidateForm) {
			this.teardownFlightSimToggleUi();
			return;
		}

		const modeCtrl = this.flightSimulatorDetails?.get("aircraftOrSimulator") as UntypedFormControl | null;
		if (!modeCtrl) return;

		const hasClassroom = !!this.classroomFreeTextControl;
		if (
			this.flightSimFeatureWiredFor === this.candidateForm &&
			this.flightSimClassroomWired === hasClassroom &&
			this.aircraftSuffixSub
		) {
			return;
		}

		this.teardownFlightSimToggleUi();
		this.flightSimFeatureWiredFor = this.candidateForm;
		this.flightSimClassroomWired = hasClassroom;

		this.flightSimMode = this.resolveFlightSimMode(modeCtrl.value);
		this.syncAircraftSuffixFromStoredReg();
		this.hydrateClassroomFromAirportRoute();
		this.syncClassroomStorageFromInput();

		// Sync UI when mode is patched from draft/API; do not clear fields here (that wipes acType on load).
		this.flightSimModeSub = modeCtrl.valueChanges
			.pipe(
				map((v) => this.resolveFlightSimMode(v)),
				distinctUntilChanged(),
			)
			.subscribe((next) => {
				this.flightSimMode = next;
				this.applyFlightSimFieldValidators();
				this.syncAircraftSuffixFromStoredReg();
				this.normalizeAcTypeControlValue();
				this.cd.detectChanges();
			});

		this.aircraftSuffixSub = this.aircraftSuffixOnly.valueChanges.subscribe((raw) => {
			if (this.flightSimDisabled || this.flightSimMode !== "aircraft") return;
			const cleaned = this.normalizeAircraftSuffixInput(raw);
			if (cleaned !== (raw ?? "").toString()) {
				this.aircraftSuffixOnly.setValue(cleaned, { emitEvent: false });
			}
			this.composeAircraftRegFromSuffix();
		});

		const freeTextCtrl = this.classroomFreeTextControl;
		if (freeTextCtrl) {
			this.classroomFreeTextSub = freeTextCtrl.valueChanges.subscribe(() => {
				if (this.flightSimMode !== "classroom") return;
				this.syncClassroomStorageFromInput();
				this.applyClassroomValidators();
				this.cd.detectChanges();
			});
		}

		this.aircraftRegistrationSyncSub = this.aircraftRegistration.valueChanges.subscribe((full) => {
			if (this.updatingAircraftRegFromSuffix) return;
			if (this.flightSimDisabled || this.flightSimMode !== "aircraft") return;
			this.patchSuffixFromFullReg(full);
		});

		this.applyFlightSimFieldValidators();
	}

	private composeAircraftRegFromSuffix(): void {
		const suf = this.normalizeAircraftSuffixInput(this.aircraftSuffixOnly.value ?? "");
		const full = this.aircraftRegPrefix ? `${this.aircraftRegPrefix}${suf}` : suf;
		this.updatingAircraftRegFromSuffix = true;
		this.aircraftRegistration.patchValue(full, { emitEvent: !this.isBootstrapping });
		this.updatingAircraftRegFromSuffix = false;
	}

	private patchSuffixFromFullReg(fullVal: unknown): void {
		const v = (fullVal ?? "").toString().trim();
		if (!this.aircraftRegPrefix) {
			this.aircraftSuffixOnly.setValue(this.normalizeAircraftSuffixInput(v), { emitEvent: false });
			return;
		}
		const tail = v.startsWith(this.aircraftRegPrefix) ? v.slice(this.aircraftRegPrefix.length) : v;
		this.aircraftSuffixOnly.setValue(this.normalizeAircraftSuffixInput(tail), { emitEvent: false });
	}

	/** Call after draft load / prefix change while in aircraft mode. */
	private syncAircraftSuffixFromStoredReg(): void {
		if (!this.showAircraftSimulatorToggle || this.flightSimMode !== "aircraft") {
			this.aircraftSuffixOnly.setValue("", { emitEvent: false });
			return;
		}
		this.patchSuffixFromFullReg(this.aircraftRegistration.value);
	}

	saveSimulatorRegistration(val: any): void {
		const s = typeof val === "string" ? val.trim() : (val?.description ?? val?.code ?? "").toString().trim();
		this.simulatorRegistration.patchValue(s, { emitEvent: !this.isBootstrapping });
		this.simulatorRegistration.markAsTouched();
	}

	markAircraftRegistrationTouched(): void {
		this.aircraftRegistration.markAsTouched();
		this.aircraftSuffixOnly.markAsTouched();
	}

	// ========== Datepicker helpers ==========
	updateDisplayDateFormat(date: Date) {
		const datePicker = this.datePickerRef?.nativeElement;
		if (datePicker && date !== null) {
			const formatted = moment(date).format(this.DATE_FORMAT.LICENSE_DATE_FORMAT);
			datePicker.value = formatted;
			this.cd.detectChanges();
		}
	}

	onDatePickerInit(datePickerRef?: ElementRef) {
		this.datePickerRef = datePickerRef;
	}

	handleDateChange(dateString: string): void {
		if (!isEmpty(dateString)) {
			this.saveLicenseExpiryDate(dateString, !this.isBootstrapping);
		}
	}

	// ========== Staff ID lookup ==========
	onFocusOut(_: any) {
		this.candidateForm.markAsTouched();
		this.isOnline = navigator.onLine;
		clearStaffLookupFailureOnValueChange(this.staffNumber);
		clearOwnStaffNumberFailure(this.staffNumber);
		this.staffNumber.updateValueAndValidity({ onlySelf: true });

		if (this.rejectOwnStaffNumber()) {
			return;
		}

		if (this.staffNumber.hasError("duplicateStaffId")) {
			this.staffNumber.markAsTouched();
		}

		if (!this.staffNumber.valid || this.staffNumber.value?.length !== 7) {
			return;
		}

		if (!navigator.onLine) return;

		this.loaderService.setLoading(true);
		this.httpService.getByStaffNumber(this.staffNumber.value).subscribe({
			next: (resp) => this.onSuccessStaffId(resp),
			error: (err) => this.onErrorStaffId(err),
		});
	}

	/** An instructor cannot enter their own staff number. */
	private rejectOwnStaffNumber(): boolean {
		if (!isInstructorOwnStaffNumber(this.staffNumber.value, this.profileService.profile?.staff_number)) {
			return false;
		}

		clearCandidateStaffDerivedFields(this.candidateForm);
		applyOwnStaffNumberFailure(this.staffNumber);
		this.loaderService.setLoading(false);
		this.cd.detectChanges();
		return true;
	}

	onSuccessStaffId(resp: { data: StaffDetails }) {
		if (!isStaffLookupResponseValid(resp) || isStaffLookupErrorPayload(resp)) {
			this.onErrorStaffId(resp);
			return;
		}

		clearStaffLookupFailure(this.staffNumber);
		const staffDetail = resp.data;

		const rankOption =
			this.sharedService.getFilteredRankOptions(this.formType).find((v) => v.code === staffDetail.rank) ?? "";

		const licenseTypeOption =
			this.sharedService.getLicenseTypeOptions(this.formType)?.find((v) => v.code === staffDetail.licenseType) ?? "";

		// Always apply the latest lookup so replacing a staff number refreshes identity fields
		this.name.patchValue(staffDetail.name, { emitEvent: false });
		this.rank.patchValue(rankOption, { emitEvent: false });
		this.licenseNo.patchValue(staffDetail.licenseNumber, { emitEvent: false });
		this.licenseType.setValue(licenseTypeOption, { emitEvent: true });
		patchStaffLicenseExpiryDate(this.licenseExpiryDate, staffDetail.licenseExpiryDate);
		applyLicenseFieldRankRules(this.candidateForm, this.formType ?? "");

		this.candidateForm.updateValueAndValidity({ emitEvent: false });

		this.datePickerInput.markAllAsTouched();
		this.candidateForm.markAsTouched();
		this.loaderService.setLoading(false);
		this.cd.detectChanges();
	}

	onErrorStaffId(error: unknown) {
		this.loaderService.setLoading(false);

		const message = this.sharedService.resolveStaffNumberLookupMessage(error);

		clearCandidateStaffDerivedFields(this.candidateForm);
		applyStaffLookupFailure(this.staffNumber);

		this.cd.detectChanges();

		setTimeout(() => {
			this.sharedService.showStaffLookupErrorModal(message);
			this.cd.detectChanges();
		}, 0);
	}

	// ========== Session → Flight/Sim disablement logic ==========
	private attachFlightSimToggleWatcher() {
		this.exemptSessionWatchSubs.forEach((sub) => sub.unsubscribe());
		this.exemptSessionWatchSubs = [];

		const sources = this.getSessionDetailsGroupsToWatch();
		for (const sd of sources) {
			this.exemptSessionWatchSubs.push(
				sd.valueChanges.subscribe(() => {
					this.applyFlightSimDisablement(this.isAnyExemptSessionSelectedAcrossWatchedCandidates());
				}),
			);
		}
		this.watchedCandidateCount = this.parentForm?.length ?? this.watchedCandidateCount;
	}

	/** A candidate added after load still has its own required aircraft type. Re-apply the session rule. */
	private watchCandidateListForExemptSessions(): void {
		if (!this.hideCandidateFields || !this.parentForm || this.candidateCountSub) return;
		this.watchedCandidateCount = this.parentForm.length;
		this.candidateCountSub = this.parentForm.valueChanges.subscribe(() => {
			const count = this.parentForm?.length ?? 0;
			if (count === this.watchedCandidateCount) return;
			this.watchedCandidateCount = count;
			this.attachFlightSimToggleWatcher();
			if (this.isTS3 || this.isTS5) {
				if (this.candidateForm) {
					for (const candidate of (this.parentForm?.controls ?? []) as UntypedFormGroup[]) {
						if (candidate === this.candidateForm) continue;
						copyCandidateSessionState(this.candidateForm, candidate);
					}
				}
				this.applyFlightSimDisablement(this.isAnyExemptSessionSelectedAcrossWatchedCandidates());
			}
		});
	}

	private getSessionDetailsGroupsToWatch(): UntypedFormGroup[] {
		if (this.hideCandidateFields && this.parentForm?.controls?.length) {
			return (this.parentForm.controls as UntypedFormGroup[])
				.map((c) => c.get("sessionDetails") as UntypedFormGroup | null)
				.filter((g): g is UntypedFormGroup => !!g);
		}
		const sd = this.sessionsDetails;
		return sd ? [sd] : [];
	}

	/**
	 * Split UI (TS1 v3 / TS3 v2 / TS5 v2): aircraft/sim block stays editable regardless of exempt sessions.
	 */
	private shouldApplyExemptSessionFlightSimDisablement(): boolean {
		return !this.showAircraftSimulatorToggle;
	}

	private isAnyExemptSessionSelectedAcrossWatchedCandidates(): boolean {
		if (!this.shouldApplyExemptSessionFlightSimDisablement()) {
			return false;
		}

		if (this.hideCandidateFields && this.parentForm?.controls?.length) {
			for (const c of this.parentForm.controls as UntypedFormGroup[]) {
				if (this.candidateHasExemptSession(c)) return true;
			}
			return false;
		}
		if (this.isTS3 || this.isTS5) {
			return this.isAnyTS3TS5ExemptSessionSelected();
		}
		return !this.isTS1 && this.isAnyExemptSessionSelected();
	}

	private candidateHasExemptSession(candidate: UntypedFormGroup): boolean {
		if (!this.shouldApplyExemptSessionFlightSimDisablement()) {
			return false;
		}

		const sd = candidate.get("sessionDetails")?.value ?? {};
		const selected: string[] = Object.values(sd).flatMap((group: any) =>
			Object.entries(group ?? {})
				.filter(([, v]) => v === true)
				.map(([k]) => k),
		);

		if (this.isTS3 || this.isTS5) {
			return selected.some((code) => {
				const normalizedCode = (code ?? "").toString().toUpperCase().trim();
				return (
					this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode) ||
					this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode.replace(/\//g, "_")) ||
					this.TS3_TS5_EXEMPT_SESSIONS.has(normalizedCode.replace(/_/g, "/"))
				);
			});
		}
		return !this.isTS1 && selected.some((code) => this.EXEMPT_SESSIONS.has((code ?? "").toString().toUpperCase()));
	}

	private isAnyExemptSessionSelected(): boolean {
		return this.getExemptHits().length > 0;
	}

	private isAnyTS3TS5ExemptSessionSelected(): boolean {
		const sd = this.sessionsDetails;
		if (!sd) return false;

		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;

			for (const item of g.items) {
				const ctrl = grp.get(item.code) as UntypedFormControl | null;
				if (!ctrl) continue;

				if (ctrl.value === true) {
					const normalized = (item.code ?? "").toString().trim().toUpperCase();
					// Check both the original format and normalized format (slash/underscore variants)
					if (
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalized) ||
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalized.replace(/\//g, "_")) ||
						this.TS3_TS5_EXEMPT_SESSIONS.has(normalized.replace(/_/g, "/"))
					) {
						return true;
					}
				}
			}
		}
		return false;
	}

	/** Old TS-3 cleared the flight fields when an exempt session was selected. */
	private clearTs3FlightValues(): void {
		const clear = (ctrl: UntypedFormControl | null | undefined) => {
			if (!ctrl) return;
			ctrl.setValue("", { emitEvent: false });
		};
		clear(this.acType);
		clear(this.sectorNo);
		clear(this.accumulatedSectors);
		clear(this.airportRoute);
		clear(this.aircraftRegistration);
		clear(this.simulatorRegistration);
		this.aircraftSuffixOnly.setValue("", { emitEvent: false });
	}

	/**
	 * Each candidate row keeps its own flight group. The shared Aircraft Details block only edits the first,
	 * so the old per-candidate session rule has to clear the others too.
	 */
	private syncExemptFlightStateToOtherCandidates(disable: boolean): void {
		if (!this.hideCandidateFields || !this.parentForm) return;

		const names = [
			"acType",
			"sectorNo",
			"accumulatedSectors",
			"airportRoute",
			"aircraftRegistration",
			"simulatorRegistration",
			"aircraftOrSimulator",
		];

		for (const candidate of this.parentForm.controls as UntypedFormGroup[]) {
			if (candidate === this.candidateForm) continue;
			const fs = candidate.get("flightSimulatorDetails") as UntypedFormGroup | null;
			if (!fs) continue;

			for (const name of names) {
				const ctrl = fs.get(name) as UntypedFormControl | null;
				if (!ctrl) continue;
				if (disable) {
					if (name !== "aircraftOrSimulator") {
						ctrl.setValue("", { emitEvent: false });
					}
					ctrl.setErrors(null);
					ctrl.clearValidators();
					if (!ctrl.disabled) ctrl.disable({ emitEvent: false });
					ctrl.markAsPristine();
					ctrl.markAsUntouched();
					ctrl.updateValueAndValidity({ emitEvent: false });
				} else if (ctrl.disabled) {
					ctrl.enable({ emitEvent: false });
				}
			}

			if (!disable) {
				this.copyPrimaryFlightDetailsTo(fs);
				if (!this.usesEts35FlightModeRules()) {
					this.applySiblingFlightValidators(fs);
				}
			}
			fs.updateValueAndValidity({ emitEvent: false });
			candidate.updateValueAndValidity({ emitEvent: false });
		}
	}

	private applySiblingFlightValidators(fs: UntypedFormGroup): void {
		if (this.usesEts35FlightModeRules()) {
			this.applyEts35FlightModeValidators(fs, this.currentFlightSimMode());
			return;
		}

		const acType = fs.get("acType") as UntypedFormControl | null;
		if (acType) {
			acType.setValidators([Validators.required]);
			acType.updateValueAndValidity({ emitEvent: false });
		}

		const mode = this.currentFlightSimMode() === "simulator" ? "simulator" : "aircraft";
		const aircraftRegistration = fs.get("aircraftRegistration") as UntypedFormControl | null;
		const simulatorRegistration = fs.get("simulatorRegistration") as UntypedFormControl | null;
		aircraftRegistration?.clearValidators();
		simulatorRegistration?.clearValidators();
		if (this.showAircraftSimulatorToggle) {
			if (mode === "aircraft") {
				aircraftRegistration?.setValidators([Validators.required]);
			} else {
				simulatorRegistration?.setValidators([Validators.required]);
			}
		}
		aircraftRegistration?.updateValueAndValidity({ emitEvent: false });
		simulatorRegistration?.updateValueAndValidity({ emitEvent: false });
	}

	private applyFlightSimDisablement(disable: boolean) {
		this.flightSimDisabled = disable;

		const modeCtrl = this.flightSimulatorDetails.get("aircraftOrSimulator") as UntypedFormControl | null;
		const controls: UntypedFormControl[] = [this.acType, this.sectorNo, this.accumulatedSectors];
		if (this.useTs1V3FlightDetails) {
			if (this.otherAirportsRoute) controls.push(this.otherAirportsRoute);
			for (const group of this.sectors.controls) {
				const g = group as UntypedFormGroup;
				const dep = g.get("departureIcao") as UntypedFormControl | null;
				const dest = g.get("destinationIcao") as UntypedFormControl | null;
				if (dep) controls.push(dep);
				if (dest) controls.push(dest);
			}
		} else {
			controls.push(this.airportRoute);
		}
		if (this.showAircraftSimulatorToggle) {
			controls.push(this.aircraftRegistration, this.simulatorRegistration);
			if (modeCtrl) {
				controls.push(modeCtrl);
			}
		} else {
			controls.push(this.aircraftSimReg);
		}

		if (disable) {
			if (this.isTS3 || this.isTS5) {
				this.clearTs3FlightValues();
			}
			controls.forEach((c) => {
				if (!c) return;
				// Clear any existing errors first
				c.setErrors(null);
				// Clear all validators - fields are NOT required when exempt sessions are selected
				c.clearValidators();
				// Disable the control (disabled controls are excluded from validation automatically)
				if (!c.disabled) c.disable({ emitEvent: false });
				// Mark as pristine and untouched
				c.markAsPristine();
				c.markAsUntouched();
				// Update validity (disabled controls are automatically valid)
				c.updateValueAndValidity({ emitEvent: false });
			});
			if (this.aircraftSuffixOnly.disabled !== true) {
				this.aircraftSuffixOnly.disable({ emitEvent: false });
			}
			this.aircraftSuffixOnly.clearValidators();
			this.aircraftSuffixOnly.updateValueAndValidity({ emitEvent: false });
			this.syncAircraftSuffixFromStoredReg();
		} else {
			controls.forEach((c) => {
				if (!c || !c.disabled) return;
				c.enable({ emitEvent: false });
			});
			if (this.aircraftSuffixOnly.disabled) {
				this.aircraftSuffixOnly.enable({ emitEvent: false });
			}
			this.applyFlightSimFieldValidators();
			this.syncAircraftSuffixFromStoredReg();
		}

		if (this.isTS3 || this.isTS5) {
			this.syncExemptFlightStateToOtherCandidates(disable);
		}

		// Update parent form groups to ensure validation state propagates
		// Use emitEvent: true for the final update to trigger parent form validation
		// Use setTimeout to ensure all changes are applied before validation
		setTimeout(() => {
			this.flightSimulatorDetails.updateValueAndValidity({ emitEvent: true });
			this.candidateForm.updateValueAndValidity({ emitEvent: true });

			// Also update the parent form array if it exists (candidates array)
			// This ensures the training info form recognizes the candidate form as valid
			if (this.parentForm) {
				this.parentForm.updateValueAndValidity({ emitEvent: true });
			}

			this.cd.detectChanges();
		}, 0);
	}

	// ========== “Save” helpers used by template ==========
	private patchUserIntent<T = any>(ctrl: UntypedFormControl, value: T) {
		ctrl.patchValue(value, { emitEvent: !this.isBootstrapping });
	}

	saveName(value: string): void {
		this.patchUserIntent(this.name as any, value);
	}
	saveRank(value: any): void {
		this.patchUserIntent(this.rank as any, value);
	}
	saveLicenseType(value: any): void {
		this.patchUserIntent(this.licenseType as any, value);
	}
	saveStaffNumber(value: string): void {
		this.patchUserIntent(this.staffNumber as any, value);
	}
	savePilotProfile(value: any): void {
		this.patchUserIntent(this.pilotProfile as any, value);
	}
	saveLicenseNo(value: string): void {
		this.patchUserIntent(this.licenseNo as any, value);
	}
	saveLicenseExpiryDate(value: string, emit = true): void {
		const newDate = moment(value).format("YYYY-MM-DD");
		this.licenseExpiryDate.patchValue(newDate, { emitEvent: emit && !this.isBootstrapping });
	}

	// Flight/Sim “save” helpers (used by the autocomplete focusOut)
	/** User switched Aircraft / Simulator — clear mode-specific fields only on explicit toggle. */
	toggleFlightSimMode(): void {
		if (this.flightSimDisabled) return;
		const next: "aircraft" | "simulator" = this.flightSimMode === "simulator" ? "aircraft" : "simulator";
		const modeCtrl = this.flightSimulatorDetails?.get("aircraftOrSimulator") as UntypedFormControl | null;
		modeCtrl?.setValue(next, { emitEvent: true });
		this.applyFlightSimModeUserChange(next);
	}

	private applyFlightSimModeUserChange(next: "aircraft" | "simulator"): void {
		this.flightSimMode = next;
		this.aircraftRegistration.setValue("", { emitEvent: false });
		this.simulatorRegistration.setValue("", { emitEvent: false });
		this.acType.setValue("", { emitEvent: false });
		this.aircraftSuffixOnly.setValue("", { emitEvent: false });
		if (next === "aircraft" && this.otherAirportsRoute) {
			this.otherAirportsRoute.setValue("", { emitEvent: false });
		}
		this.applyFlightSimFieldValidators();
		this.flightSimulatorDetails.updateValueAndValidity({ emitEvent: true });
		this.cd.detectChanges();
	}

	saveACType(val: any) {
		const normalized = resolveFlightSimAutocompleteOption(val, this.sharedService.dropDownOptionsAircraftType);
		this.acType.patchValue(normalized, { emitEvent: true });
	}

	/** Draft stores acType as a string; autocomplete needs a { code, description } option. */
	private normalizeAcTypeControlValue(): void {
		if (!this.acType) return;
		const normalized = resolveFlightSimAutocompleteOption(
			this.acType.value,
			this.sharedService.dropDownOptionsAircraftType,
		);
		if (JSON.stringify(normalized) !== JSON.stringify(this.acType.value)) {
			this.acType.setValue(normalized, { emitEvent: false });
			this.cd.detectChanges();
		}
	}

	private wireAcTypeDraftNormalization(): void {
		const fs = this.flightSimulatorDetails;
		if (!fs) return;

		fs.valueChanges
			.pipe(
				debounceTime(0),
				map((v) => v?.acType),
				distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
			)
			.subscribe(() => {
				this.normalizeAcTypeControlValue();
				this.syncAircraftSuffixFromStoredReg();
			});
	}
	saveSecNum(val: any) {
		this.sectorNo.patchValue(val, { emitEvent: true });
	}
	saveAccSec(val: any) {
		this.accumulatedSectors.patchValue(val, { emitEvent: true });
	}

	// ========== Payload helper ==========
	/**
	 * Collects selected session detail codes per group.
	 * Returns: { [groupCode]: string[] }
	 */
	getSelectedSessions(): Record<string, string[]> {
		const out: Record<string, string[]> = {};
		const sd = this.sessionsDetails;
		if (!sd) return out;

		for (const g of this.sessionGroups) {
			const grp = sd.get(g.code) as UntypedFormGroup | null;
			if (!grp) continue;

			const picked: string[] = [];
			for (const item of g.items) {
				const ctrl = grp.get(item.code) as UntypedFormControl | null;
				if (ctrl?.value === true) picked.push(item.code);
			}
			out[g.code] = picked;
		}
		return out;
	}

	// ========== Form getters used by the template ==========
	get lockCandidateIdentityFields(): boolean {
		return this.isTS1 && !this.isOnline && this.containerRatingService.inDraftMode;
	}

	get dropdownSelectOnly(): boolean {
		return this.useTs1V3FlightDetails || this.selectOnlyDropdowns;
	}

	get name() {
		return this.candidateForm.get("name")!;
	}
	get rank() {
		return this.candidateForm.get("rank")!;
	}
	get staffNumber() {
		return this.candidateForm.get("staffNumber")!;
	}
	get pilotProfile() {
		return this.candidateForm.get("pilotProfile")!;
	}
	get licenseType() {
		return this.candidateForm.get("licenseType")!;
	}
	get licenseNo() {
		return this.candidateForm.get("licenseNo")!;
	}
	get licenseExpiryDate() {
		return this.candidateForm.get("licenseExpiryDate")! as UntypedFormControl;
	}

	// Sessions root FG
	get sessionsDetails(): UntypedFormGroup {
		return this.candidateForm.get("sessionDetails") as UntypedFormGroup;
	}

	// Session remarks root FG (for text inputs per session)
	get sessionRemarks(): UntypedFormGroup {
		return this.candidateForm.get("sessionRemarks") as UntypedFormGroup;
	}

	get ets1SessionGroupCode(): string {
		return this.sessionGroups[0]?.code ?? "ts_1";
	}

	get ets1SessionsGroup(): UntypedFormGroup | null {
		return (this.sessionsDetails?.get(this.ets1SessionGroupCode) as UntypedFormGroup) ?? null;
	}

	get ets1SessionRemarksGroup(): UntypedFormGroup | null {
		return (this.sessionRemarks?.get(this.ets1SessionGroupCode) as UntypedFormGroup) ?? null;
	}

	// Flight/Sim root FG + inner controls (names must match your template)
	get flightSimulatorDetails() {
		return this.candidateForm.get("flightSimulatorDetails") as UntypedFormGroup;
	}

	/**
	 * TS-3 / TS-5 show Session Details once on the shared block.
	 * Other forms keep the checkboxes inside each candidate card.
	 */
	get shouldRenderSessionSection(): boolean {
		if (this.isTS3 || this.isTS5) {
			return this.hideCandidateFields;
		}
		return !this.hideCandidateFields;
	}

	/** Flight/simulator UI: shared block below candidates, or legacy per-panel fields. */
	get shouldRenderFlightSimulatorSection(): boolean {
		if (this.hideFlightSimulatorSection) return false;
		if (this.hideCandidateFields) return true;
		return !this.showAircraftSimulatorToggle;
	}
	get aircraftSimReg() {
		return this.candidateForm.get("flightSimulatorDetails.aircraftSimReg") as UntypedFormControl;
	}
	get aircraftRegistration() {
		return this.candidateForm.get("flightSimulatorDetails.aircraftRegistration") as UntypedFormControl;
	}
	get simulatorRegistration() {
		return this.candidateForm.get("flightSimulatorDetails.simulatorRegistration") as UntypedFormControl;
	}
	get acType() {
		return this.candidateForm.get("flightSimulatorDetails.acType") as UntypedFormControl;
	}
	get airportRoute() {
		return this.candidateForm.get("flightSimulatorDetails.airportRoute") as UntypedFormControl;
	}
	get departureIcao() {
		return this.candidateForm.get("flightSimulatorDetails.departureIcao") as UntypedFormControl;
	}
	get destinationIcao() {
		return this.candidateForm.get("flightSimulatorDetails.destinationIcao") as UntypedFormControl;
	}
	get otherAirportsRoute() {
		return this.candidateForm.get("flightSimulatorDetails.otherAirportsRoute") as UntypedFormControl;
	}
	get sectors(): UntypedFormArray {
		const existing = this.candidateForm.get("flightSimulatorDetails.sectors");
		if (existing instanceof UntypedFormArray) {
			return existing;
		}
		return applyFlightSectorsToForm(this.flightSimulatorDetails, [{ departureIcao: "", destinationIcao: "" }], {
			required: this.isTS1,
			fb: this.sectorFb,
		});
	}
	get sectorNo() {
		return this.candidateForm.get("flightSimulatorDetails.sectorNo") as UntypedFormControl;
	}
	get accumulatedSectors() {
		return this.candidateForm.get("flightSimulatorDetails.accumulatedSectors") as UntypedFormControl;
	}

	get filteredRankOptions() {
		return this.sharedService.getFilteredRankOptions(this.formType);
	}
}
